/**
 * DownloadProgressContext — coordinates bulk-download jobs.
 *
 * The flow is now job-based to match the backend (see bulkZipJobService):
 *   1. POST /bulk/prepare-zip → returns { job_id, download_token }
 *      The server starts staging files to /tmp in the background.
 *   2. GET /bulk/zip-status/:job_id polled every 1.5s → exposes
 *      staging progress (X/N files), then "ready" when the ZIP exists.
 *   3. GET /bulk/download-zip/:token → streams the pre-built ZIP.
 *      We use fetch + ReadableStream so the widget can show real bytes
 *      received (the file is on the dyno's local disk by now, so this
 *      part is uniform speed with no upstream timeouts).
 *
 * Why this lives in a context:
 *   - The widget sits in the app shell (bottom-right) so it survives
 *     page navigation and modal closes.
 *   - Anywhere that triggers a download (Media Library, File Request
 *     modal, File Request organizer, QC) goes through this single
 *     entry point so progress, dedup, and cancel behave consistently.
 *   - `isDownloadingFiles(fileIds)` lets Download buttons disable
 *     themselves while a matching job is in flight, killing the
 *     repeated-click pattern that crashed the dyno on 2026-05-04.
 *
 * Cancellation:
 *   - Local AbortController for in-flight fetches.
 *   - POST /bulk/zip-cancel/:job_id tells the server to stop staging
 *     and clean up /tmp. Called on both manual cancel (X button) and
 *     unrecoverable client errors so we never leak a server-side job.
 */

import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import { getAccessToken } from '../lib/api';

export type DownloadStatus =
  | 'preparing'      // POST /prepare-zip in flight
  | 'staging'        // server is downloading files to /tmp (X/N)
  | 'building'       // server is zipping staged files
  | 'downloading'    // streaming the finished ZIP to the browser
  | 'done'
  | 'failed';

export interface DownloadJob {
  id: string;                      // local uuid
  jobId?: string;                  // server-side job id (set after prepare)
  downloadToken?: string;          // server-side download token
  status: DownloadStatus;
  label: string;                   // shown in widget
  fileIds: string[];               // for de-dup
  fileCount: number;
  totalBytes: number;              // total source size (used for staging % and ETA)
  bytesStaged: number;             // server-side stage progress
  stagedCount: number;             // X of N files staged
  zipSize: number;                 // size of finished ZIP (set when ready)
  bytesReceived: number;           // client-side download progress (Phase 3)
  startedAt: number;
  endedAt?: number;
  errorMessage?: string;
  abortController?: AbortController;
}

interface CtxValue {
  jobs: DownloadJob[];
  activeJob: DownloadJob | null;
  isDownloadingFiles: (fileIds: string[]) => boolean;
  startDownload: (opts: {
    fileIds: string[];
    folderMap?: Record<string, string>;
    filenameOverrides?: Record<string, string>;
    label: string;
  }) => Promise<void>;
  dismiss: (jobId: string) => void;
  cancel: (jobId: string) => void;
}

const Ctx = createContext<CtxValue | null>(null);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const POLL_INTERVAL_MS = 1500;

function uid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function fileIdsKey(ids: string[]) {
  return [...ids].sort().join(',');
}

const ACTIVE_STATUSES = new Set<DownloadStatus>(['preparing', 'staging', 'building', 'downloading']);

export function DownloadProgressProvider({ children }: { children: ReactNode }) {
  const [jobs, setJobs] = useState<DownloadJob[]>([]);
  const jobsRef = useRef<DownloadJob[]>([]);
  jobsRef.current = jobs;

  // Synchronous lock — prevents the dedup race where two near-simultaneous
  // calls both pass the check before setJobs propagates. Mutated immediately,
  // so even synchronous double-calls are caught.
  const inFlightKeysRef = useRef<Set<string>>(new Set());

  const updateJob = useCallback((jobId: string, patch: Partial<DownloadJob>) => {
    setJobs(prev => prev.map(j => (j.id === jobId ? { ...j, ...patch } : j)));
  }, []);

  const cancelServerJob = useCallback(async (serverJobId: string) => {
    try {
      const token = getAccessToken();
      await fetch(`${API_BASE_URL}/media/bulk/zip-cancel/${serverJobId}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` },
      });
    } catch {
      // Best-effort. Server has its own sweep that will eventually reclaim.
    }
  }, []);

  const startDownload = useCallback(async (opts: {
    fileIds: string[];
    folderMap?: Record<string, string>;
    filenameOverrides?: Record<string, string>;
    label: string;
  }) => {
    const key = fileIdsKey(opts.fileIds);
    if (inFlightKeysRef.current.has(key)) return;
    inFlightKeysRef.current.add(key);

    const id = uid();
    const abortController = new AbortController();
    const newJob: DownloadJob = {
      id,
      status: 'preparing',
      label: opts.label,
      fileIds: opts.fileIds,
      fileCount: opts.fileIds.length,
      totalBytes: 0,
      bytesStaged: 0,
      stagedCount: 0,
      zipSize: 0,
      bytesReceived: 0,
      startedAt: Date.now(),
      abortController,
    };
    setJobs(prev => [newJob, ...prev]);

    let serverJobId: string | null = null;
    let pollTimer: any = null;

    const releaseLock = () => {
      inFlightKeysRef.current.delete(key);
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    try {
      const token = getAccessToken();

      // ── Step 1: prepare job ───────────────────────────────────────────
      const prepRes = await fetch(`${API_BASE_URL}/media/bulk/prepare-zip`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ file_ids: opts.fileIds, folder_map: opts.folderMap || null, filename_overrides: opts.filenameOverrides || null }),
        signal: abortController.signal,
      });

      if (!prepRes.ok) {
        const err = await prepRes.json().catch(() => ({}));
        if (prepRes.status === 429 && err.code === 'BULK_DOWNLOAD_ALREADY_IN_PROGRESS') {
          throw new Error('You already have a download in progress. Wait for it to finish or cancel it.');
        }
        if (prepRes.status === 413) {
          throw new Error(err.error || 'Selection exceeds bulk download limits.');
        }
        // Per-file ACL fail — server returns disallowed_files with their names so
        // the user can see exactly which selected files they don't have access to.
        if (prepRes.status === 403 && err.code === 'BULK_DOWNLOAD_ACCESS_DENIED') {
          const list = Array.isArray(err.disallowed_files) ? err.disallowed_files : [];
          const names = list.slice(0, 8).map((f: any) => f.file_name || f.file_id).join('\n  • ');
          const more = list.length > 8 ? `\n  …and ${list.length - 8} more` : '';
          throw new Error(
            `${err.error || 'You do not have access to some of the selected files.'}\n\nFiles you cannot download:\n  • ${names}${more}`
          );
        }
        throw new Error(err.error || `Prepare failed (HTTP ${prepRes.status})`);
      }

      const prepJson = await prepRes.json();
      serverJobId = prepJson?.data?.job_id;
      const downloadToken = prepJson?.data?.download_token || prepJson?.token;
      const totalBytes = prepJson?.data?.total_bytes || 0;
      if (!serverJobId || !downloadToken) {
        throw new Error('Server did not return a valid job');
      }

      updateJob(id, {
        jobId: serverJobId,
        downloadToken,
        status: 'staging',
        totalBytes,
      });

      // ── Step 2: poll status until ready ───────────────────────────────
      await new Promise<void>((resolve, reject) => {
        const poll = async () => {
          if (abortController.signal.aborted) {
            reject(Object.assign(new Error('Cancelled'), { name: 'AbortError' }));
            return;
          }
          try {
            const statusRes = await fetch(
              `${API_BASE_URL}/media/bulk/zip-status/${serverJobId}`,
              { credentials: 'include', headers: { 'Authorization': `Bearer ${token}` }, signal: abortController.signal }
            );
            if (!statusRes.ok) {
              if (statusRes.status === 404) {
                reject(new Error('Job expired or was cleaned up. Please try again.'));
                return;
              }
              reject(new Error(`Status check failed (HTTP ${statusRes.status})`));
              return;
            }
            const statusJson = await statusRes.json();
            const data = statusJson.data || {};

            const nextStatus: DownloadStatus =
              data.status === 'staging'  ? 'staging' :
              data.status === 'building' ? 'building' :
              data.status === 'ready'    ? 'building' /* keep "building" until we start the download stream */ :
              data.status === 'failed'   ? 'failed' :
              data.status === 'cancelled' ? 'failed' :
              'staging';

            updateJob(id, {
              status: nextStatus,
              stagedCount: data.staged_count || 0,
              bytesStaged: data.bytes_staged || 0,
              zipSize: data.zip_size || 0,
              totalBytes: data.total_bytes || 0,
            });

            if (data.status === 'ready') {
              resolve();
            } else if (data.status === 'failed') {
              reject(new Error(data.error || 'Server failed to prepare the ZIP'));
            } else if (data.status === 'cancelled') {
              reject(Object.assign(new Error('Cancelled'), { name: 'AbortError' }));
            }
          } catch (err: any) {
            if (err?.name === 'AbortError') {
              reject(err);
              return;
            }
            // Network blip — let the next tick retry. Don't reject.
          }
        };
        pollTimer = setInterval(poll, POLL_INTERVAL_MS);
        // Kick first poll immediately so the UI doesn't sit at 0/N for 1.5s
        poll();
      });

      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }

      // ── Step 3: stream the finished ZIP to the browser ────────────────
      updateJob(id, { status: 'downloading' });
      const dlRes = await fetch(
        `${API_BASE_URL}/media/bulk/download-zip/${downloadToken}`,
        { method: 'GET', credentials: 'include', signal: abortController.signal }
      );
      if (!dlRes.ok) {
        const err = await dlRes.json().catch(() => ({}));
        throw new Error(err.error || `Download failed (HTTP ${dlRes.status})`);
      }

      const totalDownloadBytes = parseInt(dlRes.headers.get('content-length') || '0', 10);
      updateJob(id, { totalBytes: totalDownloadBytes || 0, zipSize: totalDownloadBytes || 0 });

      const reader = dlRes.body!.getReader();
      const chunks: BlobPart[] = [];
      let received = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        if (value) {
          chunks.push(value);
          received += value.byteLength;
          updateJob(id, { bytesReceived: received });
        }
      }

      const blob = new Blob(chunks, { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `files-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      updateJob(id, { status: 'done', endedAt: Date.now() });
      // Auto-dismiss success toasts after 8s
      setTimeout(() => setJobs(prev => prev.filter(j => j.id !== id)), 8000);
    } catch (err: any) {
      const cancelled = err?.name === 'AbortError';
      const message = cancelled ? 'Cancelled' : (err?.message || 'Download failed');
      updateJob(id, { status: 'failed', errorMessage: message, endedAt: Date.now() });

      // If we have a server job, ask it to clean up so we don't leak /tmp space
      if (serverJobId) {
        cancelServerJob(serverJobId);
      }
    } finally {
      releaseLock();
    }
  }, [updateJob, cancelServerJob]);

  const dismiss = useCallback((jobId: string) => {
    setJobs(prev => prev.filter(j => j.id !== jobId));
  }, []);

  const cancel = useCallback((jobId: string) => {
    const job = jobsRef.current.find(j => j.id === jobId);
    if (!job) return;
    try { job.abortController?.abort(); } catch {}
    inFlightKeysRef.current.delete(fileIdsKey(job.fileIds));
    if (job.jobId) cancelServerJob(job.jobId);
  }, [cancelServerJob]);

  const isDownloadingFiles = useCallback((fileIds: string[]) => {
    if (!fileIds || fileIds.length === 0) return false;
    return inFlightKeysRef.current.has(fileIdsKey(fileIds));
  }, []);

  const activeJob = jobs.find(j => ACTIVE_STATUSES.has(j.status)) || null;

  return (
    <Ctx.Provider value={{ jobs, activeJob, isDownloadingFiles, startDownload, dismiss, cancel }}>
      {children}
    </Ctx.Provider>
  );
}

export function useDownloadProgress() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useDownloadProgress must be used inside DownloadProgressProvider');
  return ctx;
}
